/**
 * OrganizersListDialog
 * Shows the full list of organizers in a ritual group chat, each linking
 * to the organizer's profile.
 */

import { Link } from "react-router-dom"
import { Avatar } from "@/components/ui/avatar"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import type { RitualGroupChatParticipant } from "../../types/ritual-group-chat.types"

interface OrganizersListDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizers: RitualGroupChatParticipant[]
}

export function OrganizersListDialog({
  open,
  onOpenChange,
  organizers,
}: OrganizersListDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Organizers ({organizers.length})</DialogTitle>
        </DialogHeader>

        <div className="max-h-96 overflow-y-auto -mx-6 px-6">
          {organizers.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">No organizers</p>
          ) : (
            organizers.map(organizer => {
              const displayName = organizer.name || organizer.username || "Unknown"
              return (
                <Link
                  key={organizer.id}
                  to={`/profile/${organizer.id}`}
                  onClick={() => onOpenChange(false)}
                  className="w-full flex items-center gap-3 px-2 py-2.5 rounded-md hover:bg-accent transition-colors no-underline"
                >
                  <Avatar
                    src={organizer.profilePhoto || undefined}
                    alt={displayName}
                    fallback={displayName}
                    size="md"
                  />
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                    {organizer.username && (
                      <p className="text-xs text-muted-foreground truncate">
                        @{organizer.username}
                      </p>
                    )}
                  </div>
                </Link>
              )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
